import {
  AddressTypeDto,
  BillingUnitDto,
  CustomerWithStatusViewDto,
  GetAddressTypeQueryParams,
  GetBillingUnitQueryParams,
  GetCustomerWithStatusQueryParams,
  GetHazardousGoodsUnnumberByidQueryParams,
  GetJeopardyStatusCodeQueryParams,
  GetJeopardyStatusConfigurationQueryParams,
  GetOrderTypeQueryParams,
  GetPodQueryParams,
  GetPropertyTypeQueryParams,
  GetRevenueCodeQueryParams,
  GetSalesOrderByidAuditQueryParams,
  GetSalesOrderByidQueryParams,
  GetSalesOrderDashboardMetricsQueryParams,
  GetSalesOrderLineNoteTypeQueryParams,
  GetSalesOrderLineTypeQueryParams,
  GetSalesOrderQueryParams,
  GetSalesOrderReferenceTypeQueryParams,
  GetSalesOrderRevenueQueryParams,
  GetSalesOrderStatusQueryParams,
  GetServiceProfileNonWorkingDayExclusionQueryParams,
  GetServiceProfileQueryParams,
  GetServiceTypeQueryParams,
  GetSurchargeTypeQueryParams,
  GetUnitCodeGroupByidQueryParams,
  GetUnitCodeQueryParams,
  GetUnitTypeQueryParams,
  HazardousGoodsUnnumberDto,
  JeopardyStatusCodeDto,
  JeopardyStatusConfigurationDto,
  OrderTypeDto,
  PodDto,
  PropertyTypeDto,
  RevenueCodeDto,
  SalesOrderAuditHistoryDto,
  SalesOrderDashboardMetricsDto,
  SalesOrderDto,
  SalesOrderLineNoteTypeDto,
  SalesOrderLineTypeDto,
  SalesOrderReferenceTypeDto,
  SalesOrderRevenueDto,
  SalesOrderStatusDto,
  ServiceProfileDto,
  ServiceProfileNonWorkingDayExclusionDto,
  ServiceTypeDto,
  SurchargeTypeDto,
  UnitCodeDto,
  UnitCodeGroupDto,
  UnitTypeDto,
  GetWidgetsJeopardyStatusQueryParams,
  JeopardyStatusMetricsDto
} from 'src/models/salesorder';
import { QueryProps, baseQueryFn } from 'src/queries/baseQueryFunctions';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  customersWithStatus,
  getAddressTypes,
  getBillingUnits,
  getDashboardMetrics,
  getHazardousGoodsUnnumberById,
  getJeopardyStatusCodes,
  getJeopardyStatusConfigurations,
  getOrderTypes,
  getPod,
  getPropertyTypes,
  getRevenueCodes,
  getSalesOrderAuditHistoryById,
  getSalesOrderById,
  getSalesOrderLineNoteTypes,
  getSalesOrderLineTypes,
  getSalesOrderReferenceTypes,
  getSalesOrderRevenue,
  getSalesOrders,
  getSalesOrdersCount,
  getSalesOrderStatuses,
  getServiceCodeProfiles,
  getServiceCodes,
  getServiceProfileNonWorkingDayExclusions,
  getSurchargeCodes,
  getUnitCodeGroupById,
  getUnitCodes,
  getUnitTypes,
  getWidgetsJeopardyStatus
} from 'src/services/salesorder';

/**
 * Get Order Types query
 */
export const useGetOrderTypesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<OrderTypeDto[], GetOrderTypeQueryParams>): UseQueryResult<OrderTypeDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['orderTypes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getOrderTypes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Unit Codes query
 */
export const useGetUnitCodesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<UnitCodeDto[], GetUnitCodeQueryParams>): UseQueryResult<UnitCodeDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['unitCodes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getUnitCodes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Unit Types query
 */
export const useGetUnitTypesQuery = <T = UnitTypeDto[], R = T>({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<T, GetUnitTypeQueryParams, R>): UseQueryResult<R> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['unitTypes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getUnitTypes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res as any) })
  });
};

/**
 * Get Address Types query
 */
export const useGetAddressTypesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<AddressTypeDto[], GetAddressTypeQueryParams>): UseQueryResult<AddressTypeDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['addressTypes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getAddressTypes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Surcharges query
 */
export const useGetSurchargesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<SurchargeTypeDto[], GetSurchargeTypeQueryParams>): UseQueryResult<SurchargeTypeDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['surchargeData', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getSurchargeCodes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Sales Order Reference Types query
 */
export const useGetSalesOrderReferenceTypesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<SalesOrderReferenceTypeDto[], GetSalesOrderReferenceTypeQueryParams>): UseQueryResult<
  SalesOrderReferenceTypeDto[]
> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['referenceTypes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getSalesOrderReferenceTypes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Sales Order Line Types query
 */
export const useGetSalesOrderLineTypesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<SalesOrderLineTypeDto[], GetSalesOrderLineTypeQueryParams>): UseQueryResult<SalesOrderLineTypeDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['orderLineTypes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getSalesOrderLineTypes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Service Types query
 */
export const useGetServiceTypesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<ServiceTypeDto[], GetServiceTypeQueryParams>): UseQueryResult<ServiceTypeDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['orderServiceTypes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getServiceCodes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Sales Order by Id query
 */
export const useGetSalesOrderByIdQuery = (
  id: string,
  {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    onError = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<SalesOrderDto, GetSalesOrderByidQueryParams>
): UseQueryResult<SalesOrderDto> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['salesOrder', { id }, customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getSalesOrderById(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) }),
    ...(typeof onError === 'function' && { onError: (error: Error) => onError(error) })
  });
};

/**
 * Get Sales Order Audit History by Id query
 */
export const useGetSalesOrderAuditHistoryByIdQuery = (
  id: string,
  partnerId: string,
  {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    onError = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<SalesOrderAuditHistoryDto, GetSalesOrderByidAuditQueryParams>
): UseQueryResult<SalesOrderAuditHistoryDto> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['salesOrderAuditHistory', { id, partnerId }, customResponseModifier],
    queryFn: () =>
      baseQueryFn({ serviceFn: () => getSalesOrderAuditHistoryById(id, partnerId, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) }),
    ...(typeof onError === 'function' && { onError: (error: Error) => onError(error) })
  });
};

/**
 * Get Hazardous Goods UN Number by Id query
 */
export const useGetHazardousGoodsUnnumberById = (
  id: string,
  {
    enabled = true,
    initialData = undefined,
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<HazardousGoodsUnnumberDto, GetHazardousGoodsUnnumberByidQueryParams>
): UseQueryResult<HazardousGoodsUnnumberDto> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['hazardousGoodsUnnumber', id, customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getHazardousGoodsUnnumberById(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Property Types query
 */
export const useGetPropertyTypesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<PropertyTypeDto[], GetPropertyTypeQueryParams>): UseQueryResult<PropertyTypeDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['propertyTypes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getPropertyTypes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Revenue codes query
 */
export const useGetRevenueCodesQuery = <T = RevenueCodeDto[], R = T>({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  staleTime,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<T, GetRevenueCodeQueryParams, R>): UseQueryResult<R> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['revenueCodes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getRevenueCodes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    staleTime,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res as any) })
  });
};

/**
 * Get Sales Order Revenue query
 */
export const useGetSalesOrderRevenueQuery = ({
  enabled = false,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<SalesOrderRevenueDto[], GetSalesOrderRevenueQueryParams>): UseQueryResult<SalesOrderRevenueDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['salesOrderRevenue', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getSalesOrderRevenue(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Sales Order Line Note Types query
 */
export const useGetSalesOrderLineNoteTypesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<SalesOrderLineNoteTypeDto[], GetSalesOrderLineNoteTypeQueryParams>): UseQueryResult<
  SalesOrderLineNoteTypeDto[]
> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['salesOrderLineNoteTypes', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getSalesOrderLineNoteTypes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Sales Order Statuses query
 */
export const useGetSalesOrderStatusesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<SalesOrderStatusDto[], GetSalesOrderStatusQueryParams>): UseQueryResult<SalesOrderStatusDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['salesOrderStatuses', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getSalesOrderStatuses(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Sales Orders query
 */
export const useGetSalesOrdersQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<SalesOrderDto[], GetSalesOrderQueryParams>): UseQueryResult<SalesOrderDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['salesOrders', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getSalesOrders(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Sales Orders count query
 */
export const useGetSalesOrdersCountQuery = ({
  enabled = true,
  initialData,
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<number, GetSalesOrderQueryParams>): UseQueryResult<number> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['salesOrdersCount', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getSalesOrdersCount(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Customers with status query
 */
export const useGetCustomersWithStatusQuery = ({
  enabled = true,
  initialData,
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<CustomerWithStatusViewDto[], GetCustomerWithStatusQueryParams>): UseQueryResult<
  CustomerWithStatusViewDto[]
> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['customersWithStatus', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => customersWithStatus(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Unit Code Group by Id query
 */
export const useGetUnitCodeGroupByIdQuery = (
  id: string,
  {
    enabled = true,
    initialData,
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    customResponseModifier = undefined,
    queryKeyOverride,
    params = {}
  }: QueryProps<UnitCodeGroupDto, GetUnitCodeGroupByidQueryParams>
): UseQueryResult<UnitCodeGroupDto> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['unitCodeGroup', { id }, customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getUnitCodeGroupById(id, params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Service Code Profiles query
 */
export const useGetServiceCodeProfilesQuery = ({
  enabled,
  initialData,
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<ServiceProfileDto[], GetServiceProfileQueryParams>): UseQueryResult<ServiceProfileDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['serviceCodeProfiles', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getServiceCodeProfiles(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Billing Units query
 */
export const useGetBillingUnitsQuery = ({
  enabled,
  initialData,
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<BillingUnitDto[], GetBillingUnitQueryParams>): UseQueryResult<BillingUnitDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['billingUnits', customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getBillingUnits(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Dashboard Metrics query
 */
export const useGetDashboardMetricsQuery = ({
  enabled,
  initialData,
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  queryKeyOverride,
  params = {}
}: QueryProps<
  SalesOrderDashboardMetricsDto,
  GetSalesOrderDashboardMetricsQueryParams
>): UseQueryResult<SalesOrderDashboardMetricsDto> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['dashboardMetrics', params],
    queryFn: () => baseQueryFn({ serviceFn: () => getDashboardMetrics(params) }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Get Sales Order Pod query
 */
export const useGetSalesOrderPodQuery = ({
  enabled = false,
  initialData = {},
  onSuccess = undefined,
  onError = undefined,
  refetchOnWindowFocus = false,
  refetchOnMount = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = { Depot: '', Network: '', TrackingCode: '', Hash: '', TenantId: '' },
  retry = false
}: QueryProps<PodDto, GetPodQueryParams>): UseQueryResult<PodDto> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['order', params],
    queryFn: () => baseQueryFn({ serviceFn: () => getPod(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    refetchOnMount,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) }),
    ...(typeof onError === 'function' && { onError: error => onError(error as Error) }),
    retry
  });
};

export const useGetServiceProfileNonWorkingDayExclusionQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<
  ServiceProfileNonWorkingDayExclusionDto[],
  GetServiceProfileNonWorkingDayExclusionQueryParams
>): UseQueryResult<ServiceProfileNonWorkingDayExclusionDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['getServiceProfileNonWorkingDayExclusions', params, customResponseModifier],
    queryFn: () =>
      baseQueryFn({ serviceFn: () => getServiceProfileNonWorkingDayExclusions(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res as any) })
  });
};

/**
 * Get Jeopardy Status Codes query
 */
export const useGetJeopardyStatusCodesQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<JeopardyStatusCodeDto[], GetJeopardyStatusCodeQueryParams>): UseQueryResult<JeopardyStatusCodeDto[]> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['jeopardyStatusCodes', params, customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getJeopardyStatusCodes(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res as any) })
  });
};

/**
 * Get Jeopardy Status Configurations query
 */
export const useGetJeopardyStatusConfigurationsQuery = ({
  enabled = true,
  initialData = [],
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<JeopardyStatusConfigurationDto[], GetJeopardyStatusConfigurationQueryParams>): UseQueryResult<
  JeopardyStatusConfigurationDto[]
> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['jeopardyStatusConfigurations', params, customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getJeopardyStatusConfigurations(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res as any) })
  });
};

/**
 * Get Jeopardy Status widget metrics query
 */
export const useGetWidgetsJeopardyStatusQuery = ({
  enabled = true,
  initialData = undefined,
  onSuccess = undefined,
  refetchOnWindowFocus = false,
  customResponseModifier = undefined,
  queryKeyOverride,
  params = {}
}: QueryProps<JeopardyStatusMetricsDto, GetWidgetsJeopardyStatusQueryParams>): UseQueryResult<JeopardyStatusMetricsDto> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['widgetsJeopardyStatus', params, customResponseModifier],
    queryFn: () => baseQueryFn({ serviceFn: () => getWidgetsJeopardyStatus(params), customResponseModifier }),
    enabled,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res as any) })
  });
};
